import type {
  PowerPstyleCmsConfig,
  PowerPstyleDialogMock,
  PowerPstyleSectionCopy,
  PowerPstyleSectionSource,
} from '@/lib/power-pstyle-themes';
import {
  defaultPstyleSectionCopy,
  getPowerPstyleTheme,
  normalizePstyleSectionCopy,
  PSTYLE_DIALOG_DISPLAY_NAME,
  PSTYLE_DIALOG_FRIEND_STATUS,
  PSTYLE_DIALOG_ONLINE_STATUS,
  PSTYLE_DIALOG_USER_ID,
  resolvePowerPstyleTheme,
} from '@/lib/power-pstyle-themes';

export type { PowerPstyleCmsConfig, PowerPstyleSectionCopy } from '@/lib/power-pstyle-themes';

/** Subconjunto usado pelo CMS Pstyle (evita import circular com powers-data). */
export type PowerPstylePowerLike = PowerPstyleSectionSource;

export type PowerPstyleEditorInput = {
  customizeEnabled: boolean;
  config: PowerPstyleCmsConfig;
};

export function emptyPowerPstyleCmsConfig(
  power?: Pick<PowerPstylePowerLike, 'slug' | 'name'>
): PowerPstyleCmsConfig {
  return {
    gradient: 'linear-gradient(225deg, #633775, #361e40)',
    overlayImageUrl: '',
    overlayOpacity: 0.35,
    overlayBackgroundSize: '280px auto',
    overlayBackgroundPosition: '50% 50%',
    infoBoxBackground: 'rgba(120, 72, 140, 0.72)',
    statusText: 'online no xat',
    statusTextColor: '#361e40',
    avatarCode: 'smile',
    buttonPattern: 'none',
    sectionCopy: defaultPstyleSectionCopy(power ?? { slug: 'pstyle', name: 'Pstyle' }),
  };
}

export function emptyPowerPstyleEditorInput(
  power?: Pick<PowerPstylePowerLike, 'slug' | 'name'>
): PowerPstyleEditorInput {
  return {
    customizeEnabled: false,
    config: emptyPowerPstyleCmsConfig(power),
  };
}

function withNormalizedSectionCopy(
  config: Omit<PowerPstyleCmsConfig, 'sectionCopy'> & {
    sectionCopy?: Partial<PowerPstyleSectionCopy> | null;
  },
  power: Pick<PowerPstylePowerLike, 'slug' | 'name'>
): PowerPstyleCmsConfig {
  const fallback = defaultPstyleSectionCopy(power, getPowerPstyleTheme(power.slug));
  return {
    ...config,
    sectionCopy: normalizePstyleSectionCopy(config.sectionCopy, fallback),
  };
}

export function defaultPstyleCmsConfigForPower(
  power: Pick<PowerPstylePowerLike, 'slug' | 'name' | 'smilies' | 'description'>
): PowerPstyleCmsConfig {
  const builtIn = getPowerPstyleTheme(power.slug);
  const mock = builtIn?.mock ?? resolvePowerPstyleTheme(power).mock;
  const sectionCopy = defaultPstyleSectionCopy(power, builtIn ?? resolvePowerPstyleTheme(power));

  if (!mock) {
    return {
      ...emptyPowerPstyleCmsConfig(power),
      sectionCopy,
    };
  }

  return {
    gradient: mock.themeBackground?.gradient ?? mock.headerGradient,
    overlayImageUrl: mock.themeBackground?.overlayImageUrl ?? '',
    overlayOpacity: mock.themeBackground?.overlayOpacity ?? 0.35,
    overlayBackgroundSize: mock.themeBackground?.overlayBackgroundSize ?? '280px auto',
    overlayBackgroundPosition: mock.themeBackground?.overlayBackgroundPosition ?? '50% 50%',
    infoBoxBackground: mock.infoBoxBackground,
    statusText: mock.statusText,
    statusTextColor: mock.statusTextColor,
    avatarCode: mock.avatarCode || power.smilies?.[0] || power.slug,
    buttonPattern: mock.buttonPattern ?? 'none',
    sectionCopy,
  };
}

export function powerToPstyleEditorInput(power: PowerPstylePowerLike): PowerPstyleEditorInput {
  if (power.pstyle) {
    return {
      customizeEnabled: true,
      config: withNormalizedSectionCopy(power.pstyle, power),
    };
  }

  return {
    customizeEnabled: false,
    config: defaultPstyleCmsConfigForPower(power),
  };
}

export function pstyleEditorInputToCmsConfig(
  input: PowerPstyleEditorInput
): PowerPstyleCmsConfig | undefined {
  if (!input.customizeEnabled) return undefined;
  const copy = input.config.sectionCopy;
  return {
    ...input.config,
    gradient: input.config.gradient.trim(),
    overlayImageUrl: input.config.overlayImageUrl.trim(),
    overlayBackgroundSize: input.config.overlayBackgroundSize.trim(),
    overlayBackgroundPosition: input.config.overlayBackgroundPosition.trim(),
    statusText: input.config.statusText.trim(),
    avatarCode: input.config.avatarCode.trim() || 'smile',
    sectionCopy: {
      title: (copy?.title ?? '').trim(),
      requirement: (copy?.requirement ?? '').trim(),
      intro: (copy?.intro ?? '').trim(),
      body: '',
      tip: (copy?.tip ?? '').trim(),
    },
  };
}

export function pstyleCmsConfigToDialogMock(
  config: PowerPstyleCmsConfig,
  avatarFallback?: string
): PowerPstyleDialogMock {
  const overlayUrl = config.overlayImageUrl.trim();
  const hasOverlay = Boolean(overlayUrl);

  return {
    headerGradient: config.gradient,
    bodyBackground: '#ffffff',
    themeBackground: hasOverlay
      ? {
          gradient: config.gradient,
          overlayImageUrl: overlayUrl,
          overlayOpacity: config.overlayOpacity,
          overlayBackgroundSize: config.overlayBackgroundSize || undefined,
          overlayBackgroundPosition: config.overlayBackgroundPosition || '50% 50%',
        }
      : undefined,
    infoBoxBackground: config.infoBoxBackground,
    infoBoxTextColor: '#ffffff',
    statusBarBackground: '#ffffff',
    statusBarBorder: '#c8cad4',
    statusTextColor: config.statusTextColor,
    buttonGradient: config.gradient,
    buttonPattern: config.buttonPattern,
    avatarCode: config.avatarCode || avatarFallback || 'smile',
    displayName: PSTYLE_DIALOG_DISPLAY_NAME,
    userId: PSTYLE_DIALOG_USER_ID,
    statusText: config.statusText,
    onlineStatus: PSTYLE_DIALOG_ONLINE_STATUS,
    friendStatus: PSTYLE_DIALOG_FRIEND_STATUS,
  };
}

export function resolvePowerPstyleDialogMock(power: PowerPstylePowerLike): PowerPstyleDialogMock {
  if (power.pstyle) {
    return pstyleCmsConfigToDialogMock(power.pstyle, power.smilies?.[0] ?? power.slug);
  }

  const theme = resolvePowerPstyleTheme(power);
  const mock = theme.mock;
  if (!mock) {
    return pstyleCmsConfigToDialogMock(
      defaultPstyleCmsConfigForPower(power),
      power.smilies?.[0] ?? power.slug
    );
  }

  return {
    ...mock,
    displayName: PSTYLE_DIALOG_DISPLAY_NAME,
    userId: PSTYLE_DIALOG_USER_ID,
  };
}

/** Resolve o texto da coluna esquerda (CMS ou defaults do tema). */
export function resolvePowerPstyleSectionCopy(power: PowerPstylePowerLike): PowerPstyleSectionCopy {
  const theme = resolvePowerPstyleTheme(power);
  const fallback = defaultPstyleSectionCopy(power, theme);
  if (!power.pstyle?.sectionCopy) return fallback;
  return normalizePstyleSectionCopy(power.pstyle.sectionCopy, fallback);
}
